// Shared rich-text rendering + editing on top of Tiptap (already a web
// dependency). Used by @os/knowledgebase (articles) and @os/learning
// (RICHTEXT slides). Content is stored as Tiptap/ProseMirror JSON.
//
// No @tailwindcss/typography dependency — basic block styling is applied
// via arbitrary child selectors so headings/lists/quotes read correctly
// with the theme tokens.
import { useEditor, EditorContent, type JSONContent } from '@tiptap/react'
import { dialog } from '../../services/dialog'
import { Node, mergeAttributes } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { Bold, Italic, List, ListOrdered, Heading2, Quote, Undo, Redo, Image as ImageIcon } from 'lucide-react'

const PROSE =
  'text-foreground leading-relaxed [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2 ' +
  '[&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-3 ' +
  '[&_p]:my-2 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:my-2 ' +
  '[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground ' +
  '[&_a]:text-primary [&_a]:underline [&_code]:bg-muted [&_code]:px-1 [&_code]:rounded [&_strong]:font-semibold ' +
  '[&_img]:my-3 [&_img]:rounded-lg [&_img]:border [&_img]:border-border [&_img]:max-w-full'

const EMPTY_DOC: JSONContent = { type: 'doc', content: [] }

// Minimal image node — StarterKit ships no image, so without this KB articles
// and RICHTEXT lesson slides cannot display pictures. Uses @tiptap/core only
// (already installed) — no extra dependency. Renders a stored
// { type:'image', attrs:{ src, alt?, title? } } node as a plain <img>.
const ImageNode = Node.create({
  name: 'image',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return { src: { default: null }, alt: { default: null }, title: { default: null } }
  },
  parseHTML() {
    return [{ tag: 'img[src]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(HTMLAttributes)]
  },
})

/** Read-only renderer for stored Tiptap JSON. */
export function RichTextRenderer({ content }: { content: unknown }) {
  const editor = useEditor(
    {
      editable: false,
      extensions: [StarterKit, Link.configure({ openOnClick: true }), ImageNode],
      content: (content as JSONContent) ?? EMPTY_DOC,
      editorProps: { attributes: { class: PROSE } },
    },
    [content],
  )
  if (!editor) return null
  return <EditorContent editor={editor} />
}

interface EditorProps {
  value?: unknown
  placeholder?: string
  onChange: (payload: { json: JSONContent; text: string }) => void
}

/** Editable rich-text field with a small toolbar. */
export function RichTextEditor({ value, placeholder, onChange }: EditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: placeholder ?? 'Напишите текст…' }),
      ImageNode,
    ],
    content: (value as JSONContent) ?? EMPTY_DOC,
    editorProps: { attributes: { class: `${PROSE} min-h-[200px] focus:outline-none` } },
    onUpdate: ({ editor }) => onChange({ json: editor.getJSON(), text: editor.getText() }),
  })
  if (!editor) return null

  const Btn = ({ active, onClick, children, title }: { active?: boolean; onClick: () => void; children: React.ReactNode; title: string }) => (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`p-1.5 rounded transition ${active ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'}`}
    >
      {children}
    </button>
  )

  return (
    <div className="rounded-lg ring-1 ring-border bg-background">
      <div className="flex items-center gap-0.5 border-b border-border px-2 py-1">
        <Btn title="Жирный" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={15} /></Btn>
        <Btn title="Курсив" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={15} /></Btn>
        <Btn title="Заголовок" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={15} /></Btn>
        <Btn title="Список" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={15} /></Btn>
        <Btn title="Нумерованный" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={15} /></Btn>
        <Btn title="Цитата" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={15} /></Btn>
        <Btn title="Изображение по ссылке" onClick={async () => { const url = await dialog.prompt({ title: 'Insert image', message: 'Image URL', inputType: 'url', placeholder: 'https://...' }); if (url) editor.chain().focus().insertContent({ type: 'image', attrs: { src: url } }).run() }}><ImageIcon size={15} /></Btn>
        <div className="flex-1" />
        <Btn title="Отменить" onClick={() => editor.chain().focus().undo().run()}><Undo size={15} /></Btn>
        <Btn title="Повторить" onClick={() => editor.chain().focus().redo().run()}><Redo size={15} /></Btn>
      </div>
      <div className="px-3 py-2">
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}
